/**
 * ES6 自研接口，优化后的版本
 * @module ThemeAPI
 */
(function () {
  "use strict"; // 启用严格模式，提升代码安全性

  window.theme = window.theme || {};
  if (!window?.theme) return;

  const apiBase = window.Bloom.global.api_url;
  const gqlConfig = window.Bloom.global.graphql_api || {};
  const isIframe = window.Shopify?.isIframe;
  const gqlUrl = isIframe ? gqlConfig.url : gqlConfig.client_url;
  const gqlToken = gqlConfig.token;

  // 单例 Promise 缓存
  let loginPromise = null;
  let temporaryPromise = null;
  // 工具函数集合
  const utils = {
    /**
     * 检查 URL 是否合法
     * @param {string} url
     * @returns {boolean}
     */
    isValidUrl: (url) => /^(((ht|f)tps?):\/\/)?([^!@#$%^&*?.\s-]([^!@#$%^&*?.\s]{0,63}[^!@#$%^&*?.\s])?\.)+[a-z]{2,6}\/?/.test(url),

    /**
     * 从 localStorage 获取 token
     * @returns {string|null}
     */
    getToken: () => window.localStorage.getItem("access_token") || window.localStorage.getItem("get"),

    /**
     * 检查 token 是否过期
     * @param {string} expireTime
     * @param {number} [days=20]
     * @returns {boolean}
     */
    isTokenValid: (expireTime, days = 20) => expireTime && Date.now() < Number(expireTime) + days * 86400000,

    /**
     * 构建查询字符串
     * @param {Object} params
     * @returns {string}
     */
    buildQueryString: (params) => params ? new URLSearchParams(params).toString() : "",
    /**
     * 清空 localStorage 中的登录相关数据
     * @param {string[]} keys
     */
    clearStorage: (keys) => keys.forEach((key) => window.localStorage.removeItem(key)),
  };

  // Fetch 请求核心逻辑
  const fetchCore = {
    /**
     * 通用 fetch 请求
     * @param {string} url
     * @param {Object} [options={}]
     * @returns {Promise<Object>}
     */
    async query(url, options = {}) {
      const fullUrl = utils.isValidUrl(url) ? url : `${apiBase}${url}`;
      const token = utils.getToken();
      const method = (options.method || "GET").toUpperCase();
      const headers = {
        "Content-Type": "application/json",
        Authorization: token,
        ...options.headers,
      };
      const body = method === "POST" ? options.body || "" : undefined;

      if (
        headers["Content-Type"].match(/^application\/json$/i) &&
        fullUrl.includes("web") &&
        typeof bloomchicLib?.createSign === "function"
      ) {
        Object.assign(headers, bloomchicLib.createSign(fullUrl, body));
      }

      try {
        const response = await fetch(fullUrl, { method, headers, body, ...options });
        const result = await response.json();
        if (result.code === 401) {
          await theme.fetch.clearCustomerLogin();
          throw new Error("Token 已失效");
        }
        return result;
      } catch (error) {
        console.error("Fetch error:", error);
        throw error;
      }
    },

    /**
     * GET 请求
     * @param {string} url
     * @param {Object} [params]
     * @param {Object} [options]
     * @returns {Promise<Object>}
     */
    get(url, params, options) {
      if (params) url += `?${utils.buildQueryString(params)}`;
      return this.query(url, { method: "GET", ...options });
    },

    /**
     * POST 请求
     * @param {string} url
     * @param {Object} [data]
     * @param {Object} [options]
     * @returns {Promise<Object>}
     */
    post(url, data, options) {
      return this.query(url, {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
        ...options,
      });
    },
  };

  // 主 API 对象
  theme.fetch = {
    ...fetchCore,

    /** 清除登录信息 */
    clearLogin() {
      utils.clearStorage([
        "access_token",
        "customer_uid",
        "expire_access_token_time",
        "reality_customer",
      ]);
    },

    /**
     * 获取临时客户信息
     * @returns {Object}
     */
    getTemporaryCustomer() {
      try {
        const data = window.localStorage.getItem("temporary_customer");
        return data ? JSON.parse(data) : {};
      } catch {
        return {};
      }
    },

    /**
     * 获取临时客户 token
     * @returns {Promise<string>}
     */
    async getTemporaryCustomerToken() {
      if (temporaryPromise) return temporaryPromise;

      temporaryPromise = (async () => {
        const customer = window.localStorage.getItem("temporary_customer");
        const token = window.localStorage.getItem("temporary_token");
        const expireTime = window.localStorage.getItem("expire_temporary_token_time");

        if (
          customer &&
          token &&
          utils.isTokenValid(expireTime) &&
          customer.includes("first_order_created_at")
        ) {
          return token;
        }

        const { name, version } = getOsInfo();
        const deviceData = {
          os: name,
          os_version: version,
          device_id: sensors?.store?._state?.$device_id || "",
          time_offset: new Date().getTimezoneOffset() / 60,
          language: Shopify.locale,
          country: Shopify.country,
          platform: window.getPlatFormType(),
        };

        const result = await this.post("/user/device-register", deviceData);
        if (result?.code === 200) {
          window.localStorage.setItem("temporary_customer", JSON.stringify(result.data));
          window.localStorage.setItem("temporary_token", result.data.token);
          window.localStorage.setItem("expire_temporary_token_time", Date.now());
          return result.data.token;
        }
        throw new Error("Failed to get temporary token");
      })();

      return temporaryPromise;
    },

    /**
     * 清除客户登录状态并重新登录
     * @returns {Promise<void>}
     */
    async clearCustomerLogin() {
      utils.clearStorage([
        "reality_customer",
        "expire_access_token_time", 
        "access_token",
        "temporary_customer",
        "temporary_token",
        "expire_temporary_token_time",
      ]);
      temporaryPromise = null;
      loginPromise = null;
      await this.customerLogin();
    },

    /**
     * 客户登录
     * @returns {Promise<string>}
     */
    async customerLogin() {
      if (loginPromise) return loginPromise;

      loginPromise = (async () => {
        const email = window.global?.email;
        if (!email) {
          this.clearLogin();
          return this.getTemporaryCustomerToken();
        }

        const realityCustomer = window.localStorage.getItem("reality_customer");
        const accessToken = window.localStorage.getItem("access_token");
        const expireTime = window.localStorage.getItem("expire_access_token_time");

        if (realityCustomer && accessToken && utils.isTokenValid(expireTime)) {
          return accessToken;
        }

        const loginData = {
          token: window.global.token,
          sign: window.btoa(`email=${email}&time=${window.global.time}`),
        };
        const result = await this.post("/customer/login", loginData);

        window.localStorage.setItem("access_token", result.data.token);
        window.localStorage.setItem("expire_access_token_time", Date.now());
        window.localStorage.setItem("customer_uid", result.data.uid);
        window.localStorage.setItem("reality_customer", JSON.stringify(result.data));
        window.localStorage.removeItem("temporary_customer");

        if (sensors) {
          const siteCode = window.Bloom.global.site_code;
          sensors.login(`${siteCode !== "SPUS" ? `${siteCode}_` : ""}${result.data.uid}`);
        }
        return result.data.token;
      })();

      return loginPromise;
    },

    /**
     * UID 订阅
     * @param {string} email
     * @param {number} [type=1]
     * @returns {Promise<void>}
     */
    uidSubscribe(email, type = 1) {
      return this.post("/customer/uid-subscribe", { email, type });
    },
  };

  // GraphQL 相关逻辑
  if (window.theme && gqlUrl && gqlToken) {
    const fragments = {
      Image: `
        fragment ImageFragement on Image {
          id
          height
          width
          src
          originalSrc
          altText
        }
      `,
      PriceRange: `
        fragment PriceRangeFragement on ProductPriceRange {
          minVariantPrice { amount }
          maxVariantPrice { amount }
        }
      `,
      Variant: `
        fragment VariantFragment on ProductVariant {
          availableForSale
          id
          price { amount }
          priceV2 { amount }
          sku
          title
          quantityAvailable
          compareAtPrice { amount }
          compareAtPriceV2 { amount }
          selectedOptions { name value }
          image { url }
          metafields(identifiers: [{ key: "sales_country_list", namespace: "Product" }]) {
            key
            value
            type
          }
        }
      `,
      Option: `
        fragment OptionFragment on ProductOption {
          name
          values
        }
      `,
      Metafields: `
        fragment MetafieldsFragment on Metafield {
          key
          namespace
          value
          type
        }
      `,
    };

    const metafieldsParams = `
      [
        {key: "sku_arriva_date", namespace: "Product"},
        {key: "goods_details", namespace: "Product"},
        {key: "product_functionalFabric", namespace: "Product"},
        {key: "skc_picture_urls", namespace: "Product"},
        {key: "product_option_merger", namespace: "Product"},
        {key: "skc_attributes", namespace: "Product"},
        {key: "sku_picture_point", namespace: "Product"},
        {key: "product_activity_configs", namespace: "Product"}
      ]
    `;

    theme.gql = {
      /**
       * GraphQL 请求
       * @param {Object} data
       * @returns {Promise<Object>}
       */
      async query(data) {
        try {
          const response = await fetch(gqlUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": gqlToken,
            },
            body: JSON.stringify(data),
          });
          return response.json();
        } catch (error) {
          console.error("GQL fetch error:", error);
          throw error;
        }
      },

      /**
       * 处理商品/变体 ID
       * @param {string[]} ids
       * @param {string} type
       * @returns {string[]}
       */
      handleIds: (ids, type) =>
        ids.map((id) =>
          String(id).includes(`gid://shopify/${type}/`) ? id : `gid://shopify/${type}/${id}`
        ),

      /**
       * 获取商品数据
       * @param {string[]} ids
       * @returns {Promise<Object>}
       */
      getProducts(ids) {
        const query = `
          ${Object.values(fragments).join("\n")}
          query Products($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on Product {
                id
                handle
                title
                availableForSale
                tags
                createdAt
                updatedAt
                vendor
                featuredImage { ...ImageFragement }
                images(first: 200) { nodes { ...ImageFragement } }
                media(first: 200) {
                  nodes { ... on MediaImage { id image { ...ImageFragement } } }
                }
                priceRange { ...PriceRangeFragement }
                compareAtPriceRange { ...PriceRangeFragement }
                variants(first: 200) { nodes { ...VariantFragment } }
                options(first: 200) { ...OptionFragment }
                metafields(identifiers: ${metafieldsParams}) { ...MetafieldsFragment }
              }
            }
          }
        `;
        return this.query({ query, variables: { ids: this.handleIds(ids, "Product") } });
      },

      /**
       * 获取商品变体数据
       * @param {string[]} ids
       * @returns {Promise<Object>}
       */
      getProductsVariants(ids) {
        const query = `
          ${fragments.Metafields}
          ${fragments.PriceRange}
          query ProductVariants($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on ProductVariant {
                availableForSale
                barcode
                image { id height width url }
                currentlyNotInStock
                id
                quantityAvailable
                requiresShipping
                sku
                title
                weight
                weightUnit
                priceV2 { amount currencyCode }
                compareAtPriceV2 { amount currencyCode }
                product {
                  title
                  id
                  handle
                  tags
                  onlineStoreUrl
                  vendor
                  productType
                  availableForSale
                  priceRange { ...PriceRangeFragement }
                  compareAtPriceRange { ...PriceRangeFragement }
                  metafields(identifiers: ${metafieldsParams}) { ...MetafieldsFragment }
                }
              }
            }
          }
        `;
        return this.query({ query, variables: { ids: this.handleIds(ids, "ProductVariant") } });
      },
    };
  }
})();
